import { Button } from "@deligoo/ui"
import clsx from "clsx"
import { CSSProperties, memo, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual } from "react-redux"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppDispatch, useAppSelector } from "@/App"
import { Main } from "@/components/AppLayout"
import { Clipboard } from "@/components/Clipboard"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Status } from "@/components/Status"
import { Table, ThProps } from "@/components/Table"
import {
  ClientContextMenu,
  ClientContextMenuAction,
  ClientItemContext,
  useClientsContextMenuTrigger,
} from "@/modules/ClientContextMenu/ClientContextMenu"
import {
  clearGlobalClients,
  getClients,
  selectClientById,
  selectClientsStatus,
  selectSortedFilteredClientsIds,
} from "@/store/clients"
import { selectTeamsIds } from "@/store/teams"
import { Client } from "@/types"
import { formatPhoneNumber, truncate } from "@/utils"

import { Header } from "./partials/Header"
import { sortClientsListColumn } from "./sortClientsListColumn"

const TABLE_HEADERS = [
  "ordinal",
  "name",
  "phone",
  "address",
  "deligoo_max_distance_limit",
  "orders_unsettled_count",
] as const

export type ClientsTableHeader = typeof TABLE_HEADERS[number]

export const ClientsListView = () => {
  const [filters, setFilters] = useState<Array<(client: Client) => boolean>>([])
  const [sorter, setSorter] = useState<((a: Client, b: Client) => number) | null>(sortClientsListColumn("name", false))

  const clientsIds = useAppSelector(selectSortedFilteredClientsIds(sorter, ...filters), shallowEqual)
  const clientsStatus = useAppSelector(selectClientsStatus)
  const isTeamSelected = useAppSelector((state) => Boolean(state.teams.current))
  const teamsAvailableForUser = useAppSelector(selectTeamsIds) as number[]

  const [currentColumnSort, setCurrentColumnSort] = useState<ClientsTableHeader | null>("name")
  const [isSortedDesc, setIsSortedDesc] = useState(false)

  const [clientContextMenuAction, setClientContextMenuAction] = useState<ClientContextMenuAction>(null)

  const [isGlobal, setIsGlobal] = useState(false)

  const { t } = useTranslation()

  const dispatch = useAppDispatch()

  function onColumnSort(column: ClientsTableHeader) {
    const currentIsSortedDest = column === currentColumnSort ? !isSortedDesc : false
    const columnSortCallback = sortClientsListColumn(column, currentIsSortedDest)

    if (columnSortCallback) setSorter(columnSortCallback)
    setIsSortedDesc(currentIsSortedDest)
    setCurrentColumnSort(column)
  }

  function getThSortedBy(column: ClientsTableHeader): ThProps["sortedBy"] {
    if (currentColumnSort === column) {
      return isSortedDesc ? "desc" : "asc"
    }
  }

  useEffect(() => {
    let abortGetClients: () => void = () => null

    if (isGlobal) {
      abortGetClients = dispatch(getClients(teamsAvailableForUser)).abort
    } else {
      dispatch(clearGlobalClients())
    }

    return () => {
      abortGetClients()
      dispatch(clearGlobalClients())
    }
  }, [dispatch, isGlobal, teamsAvailableForUser])

  if (!isTeamSelected) return <Status type="no_team" title={t("places")} fullHeight />

  return (
    <Main page pageContrast>
      <ClientItemContext.Provider
        value={{
          clientContextMenuAction,
          setClientContextMenuAction,
        }}
      >
        <Header setFilters={setFilters} setIsGlobal={setIsGlobal} isGlobal={isGlobal} />

        <Table.Card>
          <Table
            minWidth="1400px"
            columns="80px minmax(200px, 350px) 200px minmax(300px, 1fr) 150px 150px 200px"
            loading={clientsStatus !== "fetched"}
            error={clientsStatus === "error"}
            empty={clientsIds.length === 0}
            virtualized
          >
            <Table.Thead>
              <Table.Tr>
                {TABLE_HEADERS.map((header) => (
                  <Table.Th
                    sortedBy={getThSortedBy(header)}
                    onClick={() => onColumnSort(header)}
                    className={clsx(
                      ["deligoo_max_distance_limit", "orders_unsettled_count"].includes(header) && "text-center"
                    )}
                    key={header}
                  >
                    {t(`clients_list_headers.${header}`)}
                  </Table.Th>
                ))}
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeList
                    height={height}
                    width={width}
                    overscanCount={4}
                    itemData={clientsIds}
                    itemCount={clientsIds.length}
                    itemKey={(index, data) => data[index]}
                    itemSize={110}
                  >
                    {ClientsListRow}
                  </FixedSizeList>
                )}
              </AutoSizer>
            </Table.Tbody>
          </Table>
        </Table.Card>

        <ClientContextMenu />
      </ClientItemContext.Provider>
    </Main>
  )
}

type ClientsListRowProps = {
  data: Array<Client["id"]>
  index: number
  style: CSSProperties
}

const ClientsListRow = memo(({ data, index, style }: ClientsListRowProps) => {
  const clientId = data[index]

  return <ClientsListItem key={clientId} clientId={clientId} style={style} index={index} />
}, areEqual)

type ClientsListItemProps = {
  clientId: Client["id"]
  index: number
  style: CSSProperties
}

const ClientsListItem = ({ clientId, index, style }: ClientsListItemProps) => {
  const client = useAppSelector(selectClientById(clientId))

  const { setClientContextMenuAction } = useContext(ClientItemContext) || {}

  const contextMenuRef = useClientsContextMenuTrigger({ clientId })

  const { t } = useTranslation("clients")

  if (!client || !setClientContextMenuAction) return null

  return (
    <Table.Tr style={style} ref={contextMenuRef}>
      <Table.Td>{index + 1}</Table.Td>

      <Table.Td>
        {truncate(client.name, 75)}
        <div className="mt-1 text-desc text-muted">{t(`location_types.${client.location_type}`)}</div>
      </Table.Td>

      <Table.Td>
        <Clipboard>{formatPhoneNumber(client.phone)}</Clipboard>
      </Table.Td>

      <Table.Td>{client.address || "-"}</Table.Td>

      <Table.Td className="text-center">{client.deligoo_max_distance_limit} km</Table.Td>

      <Table.Td className="text-center">{client.orders_unsettled_count}</Table.Td>

      <Table.Td className="text-right">
        <Button
          size="small"
          onClick={() => setClientContextMenuAction({ action: "show_details", clientId: client.id })}
        >
          Szczegóły
        </Button>

        <ContextMenu.Button className="ml-3" onClick={openContextMenu} />
      </Table.Td>
    </Table.Tr>
  )
}
